import { useDragLayer } from 'react-dnd';
import { cn } from '@design-system';
import { GripVertical, Layers, LayoutList, MessageSquare, FileText } from 'lucide-react';
import { DND_TYPES, type SortableDragItem } from '@/features/quote-and-apply-builder/hooks/use-sortable-item';

const typeConfig: Record<string, { icon: typeof Layers; color: string; bgColor: string }> = {
  [DND_TYPES.SECTION]: { icon: Layers, color: 'text-primary', bgColor: 'bg-primary/5 border-primary/20' },
  [DND_TYPES.SUBSECTION]: { icon: LayoutList, color: 'text-primary', bgColor: 'bg-primary/5 border-primary/20' },
  [DND_TYPES.QUESTION_GROUP]: { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' },
  [DND_TYPES.QUESTION]: { icon: FileText, color: 'text-gray-700', bgColor: 'bg-white border-gray-300' },
};

export function FormBuilderDragLayer() {
  const { isDragging, item, currentOffset } = useDragLayer(monitor => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as SortableDragItem | null,
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !item || !currentOffset) return null;

  const config = typeConfig[item.type];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className="pointer-events-none fixed inset-0 z-[100]">
      <div
        style={{
          transform: `translate(${currentOffset.x - 20}px, ${currentOffset.y - 16}px)`,
        }}
      >
        <div
          className={cn(
            'flex max-w-64 items-center gap-2 rounded-lg border px-3 py-2 shadow-lg',
            config.bgColor,
          )}
          style={{ transform: 'rotate(2deg)' }}
        >
          <GripVertical className="size-3.5 shrink-0 text-gray-400" />
          <Icon className={cn('size-3.5 shrink-0', config.color)} />
          <span className="truncate text-sm font-medium text-gray-900">
            {item.label || 'Untitled'}
          </span>
        </div>
      </div>
    </div>
  );
}
